/**
 * The SoundCheckBox brand mark — one shared inline-SVG glyph + a styled wordmark, reused
 * everywhere the brand appears: the shared app header (`app-header.ts`), the editor topbar, and
 * the landing nav (owner ask 2026-07-22 #7 — replace the old raster wordmark,
 * `public/landing/logo.png`, 191×50, with "a vector logo based on the old one, but not as ugly").
 *
 * Inline SVG for the MARK only (no external asset/network request, themeable via `currentColor` —
 * same philosophy as `components/icons.ts`); the wordmark is real styled text (`<span>`s), not SVG
 * `<text>` — sharper rendering, no font-in-SVG quirks, and it inherits `.landing`'s Montserrat
 * stack for free wherever that's in scope.
 *
 * --- What the mark should mean (owner, 2026-07-22, correcting an earlier first-draft guess;
 * expanded 2026-07-22 follow-up: "не хочу ограничивать фантазию") -------------------------------
 * OWNER DECISION 2026-07-22 (after reviewing six candidates in `apps/web/scripts/logo-preview.html`):
 * **`LOGO_MARK_CHECKBOX` ships** — "всё равно только один в виде галочки чекбокса подходит". Two
 * runners-up are kept in reserve, everything else was deleted (the jack-plug pair and the monitor
 * wedge lived in git history if ever wanted back). Swapping the shipped mark stays a one-line change
 * (`SHIPPED_MARK` below) — nothing that imports this module needs to change.
 *
 *  1. `LOGO_MARK_CHECKBOX` (SHIPPED) — a rounded-square checkbox whose checkmark tail trails into a
 *     couple of soundwave ticks. The most literal pun on the product's own name ("check" a box),
 *     playful, reads well at very small sizes.
 *  2. `LOGO_MARK_WAVEFORM` (reserve) — five vertical bars, a level meter/EQ: the ACT of
 *     sound-checking rather than an object. The strongest non-pun alternative.
 *  3. `LOGO_MARK_CASE` (reserve) — the flight case on caster wheels: the literal road case the
 *     name also means (owner's own gloss of the brand).
 *
 * All marks share the same 26×28 viewBox and stroke language, so switching `SHIPPED_MARK` is a
 * drop-in swap — no other file needs to change.
 * -------------------------------------------------------------------------------------------- */

import { t } from "../i18n";

const MARK_VIEWBOX = "0 0 26 28";

/** SHIPPED (owner pick 2026-07-22): a checkbox whose check trails into soundwave ticks. */
export const LOGO_MARK_CHECKBOX =
  '<rect x="2" y="6" width="17" height="17" rx="4"/>' +
  '<path d="M6 15l3.4 3.4L16 11.6" stroke-width="2.2"/>' +
  '<path d="M21 12v4M23.5 10v8M26 13v2" opacity="0.55"/>';

/** Reserve: a 5-bar level meter/waveform — the act of a sound check, not an object. Bars are thick
 *  round-capped strokes (not `<rect>`s) so they read as solid pills consistent with the icon set. */
export const LOGO_MARK_WAVEFORM =
  '<path d="M3 18v-8" stroke-width="3"/>' +
  '<path d="M8.5 23v-18" stroke-width="3"/>' +
  '<path d="M14 20v-12" stroke-width="3"/>' +
  '<path d="M19.5 25v-22" stroke-width="3"/>' +
  '<path d="M25 17v-6" stroke-width="3"/>';

/** Reserve: case body + lid seam + corner-latch ticks + center hasp + two filled wheels — the road
 *  case the name also literally means. */
export const LOGO_MARK_CASE =
  '<path d="M4 4h18a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1z"/>' +
  '<path d="M3 9h20"/>' +
  '<path d="M6.5 4V2M19.5 4V2"/>' +
  '<rect x="10.5" y="11.5" width="5" height="4" rx="0.7"/>' +
  '<circle cx="8" cy="23" r="2" fill="currentColor" stroke="none"/>' +
  '<circle cx="18" cy="23" r="2" fill="currentColor" stroke="none"/>';

/** The mark actually shipped — the owner's 2026-07-22 pick. Change this one line to try a reserve
 *  (preview them first: `apps/web/scripts/logo-preview.html`). */
const SHIPPED_MARK = LOGO_MARK_CHECKBOX;

/** The mark alone, as raw `<svg>` markup (icons.ts's `icon()` calling convention). `sizePx` sets the
 *  rendered WIDTH; height follows the mark's own 26:28 aspect ratio so it's never squashed. */
export function brandMarkSvg(sizePx = 26): string {
  const height = Math.round((sizePx * 28) / 26);
  return `<svg class="brand-logo-mark" width="${sizePx}" height="${height}" viewBox="${MARK_VIEWBOX}" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${SHIPPED_MARK}</svg>`;
}

/** The three-tone wordmark ("Sound" light / "Check" bold ink / "Box" bold accent) — real text, not
 *  SVG, so it stays crisp and inherits whatever font stack is in scope. `aria-hidden` on the mark
 *  above + this real text is what makes the whole lockup read correctly to a screen reader; the
 *  wrapping `<a>`/`<span>` (see below) carries the one accessible name. */
function brandWordmarkHtml(): string {
  return (
    '<span class="brand-logo-text">' +
    '<span class="bl-sound">Sound</span>' +
    '<span class="bl-check">Check</span>' +
    '<span class="bl-box">Box</span>' +
    "</span>"
  );
}

/** The full lockup (mark + wordmark), NOT wrapped in a link — for contexts that already provide
 *  their own `<a>`/click-handling (the landing nav keeps its existing scroll-to-top anchor). */
export function brandLogoHtml(sizePx = 26): string {
  return `<span class="brand-logo">${brandMarkSvg(sizePx)}${brandWordmarkHtml()}</span>`;
}

/** The full lockup wrapped in its own link to the landing page — for chrome that doesn't already
 *  have one (the shared app header, the editor topbar). Callers still need to wire the click (this
 *  module doesn't import the router, to keep it a pure markup/no-dependency leaf like `icons.ts`);
 *  select `.brand-logo-link` and call `event.preventDefault()` + `navigate({ name: "landing" })`. */
export function brandLogoLinkHtml(sizePx = 26): string {
  return `<a href="/" class="brand-logo-link" aria-label="${t("common.brandHomeAriaLabel")}">${brandLogoHtml(sizePx)}</a>`;
}
